import { ORDER_TYPE_CODE } from '../utils/enum/OrderTypeCode';
import { removeUndefinedAndNullFromObject } from '../utils/functions';
import { CartModelLoad, CartModelSave } from '../utils/models/account';
import { CartDataItems, CsvInfo, Prices, PricesResponse, TCalculatedOrderTotal } from '../utils/models/cart';
import { BcDocumentHistory, DocumentHistoryOptionalParams } from '../utils/models/documents';
import { BcFinancialInfo, OrderInvoices } from '../utils/models/financial';
import {
    BcOrder,
    BcOrderHistory,
    BcOrderShippingStatuses,
    BcOrderType,
    BcShipmentDates,
    OrderDataObject,
    ShopifyOrder,
} from '../utils/models/order';
import { BcProductBrand } from '../utils/models/product';

export const createBcOrder = (orderData: BcOrder) => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');
        orderData = { ...orderData, shopifyAccountEmail: window.errea_accountUserData.email };
        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({ orderData: orderData }),
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/order/open_draft', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (result.documentNo) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const confirmBcOrder = (documentNo: string, documentType: number, email: string): Promise<string> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
                documentNo: documentNo,
                documentType: documentType,
                email: email,
                language: window.errea_accountUserData.language ?? 'it',
            }),
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/order/confirm', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result.value);
                } else {
                    reject(result.error ? result.error : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const confirmAsyncOrder = (documentNo: string, documentType: number, email: string): Promise<string> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
                documentNo: documentNo,
                documentType: documentType,
                email: email,
                language: window.errea_accountUserData.language ?? 'it',
            }),
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/order/confirm_async', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result.pid);
                } else {
                    reject(result.error ? result.error : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const createPromoBcPriceOrder = (orderData: BcOrder) => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');
        orderData = { ...orderData, shopifyAccountEmail: window.errea_accountUserData.email };
        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({ orderData }),
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/order/open_draft', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (result.documentNo) {
                    resolve(result.documentNo);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const createShopifyOrder = (orderData: ShopifyOrder) => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({ orderData }),
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/back_office/order/open_draft', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (result) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const calcBcPromoOrder = (documentNo: string) => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({ documentNo }),
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/order/promo/calculate_request', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const getBcOrderLines = (documentNo: string) => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({ documentNo }),
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/order/promo/calculated_lines', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const getAsyncBcOrderTotal = (documentNo: string, documentType: string) => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const payload = {
            documentType: documentType,
            documentNo: documentNo,
        };

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(payload),
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/order/catalog/total_lines_async', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const getAsyncBcOrderTotalStatus = (asyncRequestPid: string) => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'POST',
            headers: headers,
            redirect: 'follow' as RequestRedirect,
        };

        fetch(`/apps/errea/order/catalog/total_lines_async_status?pid=${asyncRequestPid}`, requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const getOrderTotals = (asyncRequestPid: string) => {
    return new Promise<TCalculatedOrderTotal>((resolve, reject) => {
        const loopInterval = setInterval(async () => {
            const orderTotalsResponse = (await getAsyncBcOrderTotalStatus(asyncRequestPid)) as {
                status: string;
                errors: Record<string, any>;
                bcResponse: TCalculatedOrderTotal | null;
            };

            if (orderTotalsResponse.status === 'done') {
                clearInterval(loopInterval);

                if (Object.keys(orderTotalsResponse.errors).length > 0 || !orderTotalsResponse.bcResponse?.value) {
                    reject('No order totals response received');
                    return;
                }

                resolve(orderTotalsResponse.bcResponse);
            }
        }, 1000);
    });
};

export const getCalculatedLinePrice = (documentNo: string, documentType: string, documentLineNo: number): Promise<Prices> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');
        const payload = {
            documentType,
            documentNo,
            documentLineNo
        };

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(payload),
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/order/calculated_line_price', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    const castedResult = result as PricesResponse;
                    resolve(castedResult.value);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const getAsyncCombinedProducts = (
    collectionName: string,
    documentNo: string,
    accountNo: string,
    customerType: string,
    cartType: 'catalog' | 'promo',
) => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');
        const agentId =
            window.errea_accountUserData.accountType != 'Customer' ? window.errea_accountUserData.accountCode : '';
        const payload = {
            collectionName,
            documentNo,
            accountNo,
            customerType,
            cartType,
            agentId,
        };

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(payload),
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/order/combined/calculate_async', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const getPromoAsyncProducts = (
    collectionName: string,
    documentNo: string,
    accountNo: string,
    customerType: string,
    cartType = 'promo',
) => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');
        const agentId =
            window.errea_accountUserData.accountType != 'Customer' ? window.errea_accountUserData.accountCode : '';
        const payload = {
            collectionName,
            documentNo,
            accountNo,
            customerType,
            cartType,
            agentId,
        };

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(payload),
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/order/promo/calculate_async', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const getAsyncBcPromoOrderTotal = (documentNo: string, documentType: string) => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const payload = {
            documentType: documentType,
            documentNo: documentNo,
        };

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(payload),
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/order/promo/total_lines_async', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const getCustomerReference = (customerReference: string) => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const payload = {
            customerReference: customerReference,
        };

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(payload),
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/order/customer_reference', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const getOrderHistory = (
    customerType: string,
    accountId: string,
    documentType: string,
    pageSize?: string,
    fromCursor?: string,
): Promise<BcOrderHistory[]> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const payload = {
            customerType,
            accountId,
            documentType,
            pageSize,
            fromCursor,
        };

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(payload),
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/personal_area/order_history', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result.value);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const getOrderDetailsData = (
    accountId: string,
    searchDocumentNo: string,
    docType: string,
): Promise<BcOrderHistory[]> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const payload = {
            customerType: 'Customer',
            documentType: docType,
            accountId,
            searchDocumentNo,
        };

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(payload),
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/personal_area/order_history', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result.value);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const getFinancialInfos = (customerNo: string): Promise<BcFinancialInfo> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const payload = {
            customerNo,
        };

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(payload),
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/personal_area/financial_situation', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const getDocumentHistory = (
    customerNo: string,
    pageSize?: string,
    fromCursor?: string,
): Promise<OrderInvoices> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const payload = {
            customerType: 'Customer',
            documentType: 'Invoice',
            accountId: customerNo,
            pageSize,
            fromCursor,
        };

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(payload),
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/personal_area/document_history', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const importCsvAsync = (
    accountNo: string,
    file: File,
    cartType: string,
    campaignCode?: string,
    promoCode?: string,
    collectionName?: string,
): Promise<CsvInfo> => {
    return new Promise((resolve, reject) => {
        const agentId =
            window.errea_accountUserData.accountType != 'Customer' ? window.errea_accountUserData.accountCode : '';

        const formData = new FormData();
        formData.append('order', file);
        formData.append('accountNo', accountNo);
        formData.append('agentId', agentId);
        formData.append('customerType', window.errea_accountUserData.accountType);
        formData.append('cartType', cartType);

        if (collectionName) formData.append('collectionName', collectionName);
        if (campaignCode) formData.append('campaignCode', campaignCode);
        if (promoCode) formData.append('promoCode', promoCode);

        fetch('/apps/errea/order/import_csv_async', {
            method: 'POST',
            body: formData,
        })
            .then((response) => response.json())
            .then((result) => {
                resolve(result);
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const getInventoryAvailability = (sku: string, size: string) => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const productData = {
            itemNo: sku,
            variantCode: size,
        };

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(productData),
            redirect: 'follow' as RequestRedirect,
        };

        fetch(`/apps/errea/products/inventory_availability`, requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (result.error) {
                    reject(result);
                }
                resolve(result);
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const getBcDocumentHistory = (
    customerType: string,
    accountId: string,
    documentType: string,
    pageSize?: number,
    fromCursor?: string,
    params?: DocumentHistoryOptionalParams,
): Promise<BcDocumentHistory[]> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        let payload = {
            customerType,
            accountId,
            documentType,
            pageSize,
            fromCursor,
        };

        if (params !== null) {
            payload = { ...payload, ...removeUndefinedAndNullFromObject(params as Record<string, any>) };
        }

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(payload),
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/personal_area/document_history', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result.value);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const getBcOrderTypes = (): Promise<BcOrderType[]> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'GET',
            headers: headers,
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/order/order_types', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const getShippingDates = (productCode: string): Promise<BcShipmentDates> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'GET',
            headers: headers,
            redirect: 'follow' as RequestRedirect,
        };
        fetch(`/apps/errea/products/delivery_dates?baseModelCode=${productCode}`, requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    })
}

export const getShippingDatesBySku = (sku: string): Promise<BcShipmentDates> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'POST',
            headers: headers,
            redirect: 'follow' as RequestRedirect,
            body: JSON.stringify({ itemNo: sku }),
        };
        fetch(`/apps/errea/products/delivery_dates_by_brand`, requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    })
}

export const getBcOrderShippingStatuses = (): Promise<BcOrderShippingStatuses[]> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'GET',
            headers: headers,
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/order/shipping_statuses', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const getBcProductsBrands = (): Promise<BcProductBrand[]> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'GET',
            headers: headers,
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/products/brands', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const saveCartOnDb = (products: CartDataItems[]) => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const selectedCustomer = localStorage.getItem('errea_selectedCustomer')
            ? JSON.parse(localStorage.getItem('errea_selectedCustomer') as string)
            : null;

        if (selectedCustomer) {
            const body = {
                customerId: selectedCustomer.customerNo ?? '',
                agentId:
                    window.errea_accountUserData.accountType != 'Customer'
                        ? window.errea_accountUserData.accountCode
                        : '',
                customerType: window.errea_accountUserData.accountType,
                label: '',
                currency: selectedCustomer.currencyCode ?? 'EUR',
                items: products,
            };

            const requestOptions = {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(body),
                redirect: 'follow' as RequestRedirect,
            };
            try {
                fetch('/apps/errea/carts/current', requestOptions)
                    .then(() => {
                        resolve(undefined);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            } catch (error) {
                reject(error);
            }
        }
    });
};

export const getDocumentDetails = (
    customerNo: string,
    searchDocumentNo: string,
    documentType: string,
    customerType: string,
): Promise<OrderInvoices> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const payload = {
            customerType,
            searchDocumentNo,
            documentType,
            accountId: customerNo,
        };

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(payload),
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/personal_area/document_history', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const deleteDraftOrder = (orderNo: string) => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'DELETE',
            headers: headers,
            redirect: 'follow' as RequestRedirect,
        };

        fetch(`/apps/errea/order/delete_draft?id=${orderNo}`, requestOptions)
            .then((response) => {
                if (response.status != 204) {
                    reject('error');
                } else {
                    resolve(true);
                }
            })
            .catch((error) => reject(error));
    });
};

export const editDraftOrder = (documentNo: string, etag: string | null, orderData: OrderDataObject) => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const body = {
            etag,
            documentNo,
            orderData,
        };

        const requestOptions = {
            method: 'PATCH',
            headers: headers,
            body: JSON.stringify(body),
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/order/update_draft', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const loadCartModel = (customerNo: string, agentId?: string): Promise<CartModelLoad[] | string> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'GET',
            headers: headers,
            redirect: 'follow' as RequestRedirect,
        };

        const url = agentId
            ? `/apps/errea/carts/list?customerId=${customerNo}&agentId=${agentId}`
            : `/apps/errea/carts/list?customerId=${customerNo}`;

        fetch(url, requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const saveCartModel = (cart: CartModelSave) => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(cart),
            redirect: 'follow' as RequestRedirect,
        };

        fetch('/apps/errea/carts/save', requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const deleteCartModel = (id: number) => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'DELETE',
            headers: headers,
            redirect: 'follow' as RequestRedirect,
        };

        fetch(`/apps/errea/carts/delete?id=${id}`, requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const downloadExcelOrderOutdated = (id: string, email: string): Promise<string> => {
    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'POST',
            headers: headers,
            redirect: 'follow' as RequestRedirect,
            body: JSON.stringify({ customer_code: id, customer_email: email }),
        };

        fetch(`/apps/errea/order/order_line_export_excel`, requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error && result.status == 'running') {
                    resolve(result);
                } else {
                    reject(result.error ? result.error.code : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
}

export const getCalculatedPrice = async (accountNo: string, sku: string, size?: string, orderType?: ORDER_TYPE_CODE) => {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');


    let productData: any = {
        accountNo,
        itemNo: sku,
        variantCode: size ?? '',
        orderTypeCode: orderType ?? ORDER_TYPE_CODE.Vendi,
    };

    const requestOptions = {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(productData),
        redirect: 'follow' as RequestRedirect,
    };

    const pricesResponse = await fetch(`/apps/errea/products/calculated_price`, requestOptions)
        .then((response) => response.json())
        .catch((error) => {
            throw error ? error : 'error';
        });

    return pricesResponse as PricesResponse;
};
